import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CancelButton } from 'src/components/Buttons';
import {
  Content,
  useContent,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { useAccountingEventExport } from 'src/hooks/api/useAccountingEventExport';
import { useAccountingEventLastRefreshTime } from 'src/hooks/api/useAccountingEventLastRefreshTime';
import { useAccountingEventPreview } from 'src/hooks/api/useAccountingEventPreview';
import { LoadingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { downloadFileFromUrl } from 'src/utils/fileUtils';

import {
  formatDateForDisplay,
  getDateRangeError,
  toDateStr,
} from '../AccountingEvent/AccountingEventOnDemandExportDialog.utils';
import * as styles from './AccountingAuditExport.css';

type ExportMode = 'export' | 'preview';

export interface StreamOption {
  value: string;
  label: ReactNode;
}

const defaultStreamOptions: StreamOption[] = [
  { value: 'invoice', label: <Content id={ContentId.ExportInvoice} /> },
];

export interface AccountingAuditExportProps {
  entityId: number;
  isOpen: boolean;
  onClose: () => void;
  streamOptions?: StreamOption[];
}

export const AccountingAuditExport = ({
  entityId,
  isOpen,
  onClose,
  streamOptions,
}: AccountingAuditExportProps) => {
  const resolvedStreamOptions = streamOptions ?? defaultStreamOptions;

  const [mode, setMode] = useState<ExportMode>('export');
  const [selectedStream, setSelectedStream] = useState<string>(
    resolvedStreamOptions[0].value
  );
  const [createDateFrom, setCreateDateFrom] = useState<string>('');
  const [createDateTo, setCreateDateTo] = useState<string>('');

  // ── Last refresh time (data availability) ───────────────────────────────────
  const { data: lastRefreshTime, isLoading: isLoadingRefreshTime } =
    useAccountingEventLastRefreshTime(selectedStream);

  const maxDateStr = lastRefreshTime ? toDateStr(lastRefreshTime) : undefined;

  // ── Date range validation ────────────────────────────────────────────────────
  const endRequired = useContent(ContentId.ExportDateRangeEndRequired);
  const startRequired = useContent(ContentId.ExportDateRangeStartRequired);
  const startBeforeEnd = useContent(ContentId.ExportDateRangeStartBeforeEnd);
  const exceedsMaxDays = useContent(ContentId.ExportDateRangeMax90Days);
  const exceedsAvailableData = useFormattedContent(
    FormatContentId.ExportDateRangeExceedsAvailableData,
    [maxDateStr ? formatDateForDisplay(maxDateStr) : '']
  );

  const dateRangeError = getDateRangeError(
    createDateFrom,
    createDateTo,
    {
      endRequired,
      startRequired,
      startBeforeEnd,
      exceedsMaxDays,
      exceedsAvailableData: exceedsAvailableData as string,
    },
    maxDateStr
  );
  const canExport = !dateRangeError && !!createDateFrom && !!createDateTo;

  // ── Accounting Events export ────────────────────────────────────────────────
  const { startExport, isExporting, hasFeature } = useAccountingEventExport({
    stream: selectedStream,
    createDateFrom: createDateFrom || undefined,
    createDateTo: createDateTo || undefined,
    entityId,
  });

  const exportStartedRef = useRef(false);
  useEffect(() => {
    if (isExporting) {
      exportStartedRef.current = true;
    } else if (exportStartedRef.current) {
      exportStartedRef.current = false;
      onClose();
    }
  }, [isExporting, onClose]);

  // ── Event Preview CSV export ────────────────────────────────────────────────
  const {
    mutate: fetchPreview,
    data: previewData,
    isPending: isPreviewPending,
    isError: isPreviewError,
    reset: resetPreview,
  } = useAccountingEventPreview(entityId, selectedStream);

  const previewErrorFallback = useContent(ContentId.AccountingPreviewLoadError);

  // Trigger browser download and auto-close as soon as the URL is ready
  useEffect(() => {
    if (previewData?.downloadUrl) {
      downloadFileFromUrl(
        `accounting-preview-sale-${entityId}.csv`,
        previewData.downloadUrl
      );
      setMode('export');
      resetPreview();
      onClose();
    }
  }, [previewData?.downloadUrl, entityId, onClose, resetPreview]);

  // ── Shared handlers ─────────────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    setMode('export');
    resetPreview();
    onClose();
  }, [onClose, resetPreview]);

  const isBusy = isExporting || isPreviewPending;

  if (!hasFeature) {
    return null;
  }

  return (
    <GenericDialog
      size="md"
      isOpen={isOpen}
      onCancel={isBusy ? undefined : handleCancel}
      header={<Content id={ContentId.Export} />}
      footer={
        <>
          <CancelButton disabled={isBusy} onClick={handleCancel} />
          {mode === 'preview' ? (
            <Button
              variant="regular"
              disabled={isPreviewPending}
              onClick={() => fetchPreview()}
            >
              {isPreviewPending ? (
                <span className={styles.exportButtonContent}>
                  <LoadingIcon className={styles.exportSpinner} />
                  <Content id={ContentId.Exporting} />
                </span>
              ) : (
                <Content id={ContentId.Export} />
              )}
            </Button>
          ) : (
            <Button
              variant="regular"
              disabled={
                isExporting || !canExport || isLoadingRefreshTime || !maxDateStr
              }
              onClick={() => startExport()}
            >
              {isExporting ? (
                <span className={styles.exportButtonContent}>
                  <LoadingIcon className={styles.exportSpinner} />
                  <Content id={ContentId.Exporting} />
                </span>
              ) : (
                <Content id={ContentId.Export} />
              )}
            </Button>
          )}
        </>
      }
    >
      <div className={styles.dialogBody}>
        {/* Mode selector */}
        <RadioGroup
          value={mode}
          onValueChange={(value) => {
            setMode(value as ExportMode);
            resetPreview();
          }}
        >
          <Stack direction="row" gap="xl">
            <Radio
              value="export"
              disabled={isBusy}
              label={<Content id={ContentId.AccountingExportModeExport} />}
            />
            <Radio
              value="preview"
              disabled={isBusy}
              label={<Content id={ContentId.AccountingExportModePreview} />}
            />
          </Stack>
        </RadioGroup>

        {/* Stream selector — always visible when multiple stream options exist */}
        {resolvedStreamOptions.length > 1 && (
          <RadioGroup
            value={selectedStream}
            onValueChange={(value) => {
              setSelectedStream(value);
              resetPreview();
            }}
          >
            <Stack direction="row" gap="xl">
              {resolvedStreamOptions.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  disabled={isBusy}
                  label={option.label}
                />
              ))}
            </Stack>
          </RadioGroup>
        )}

        {/* Accounting Events mode — date range + refresh hint */}
        {mode === 'export' && (
          <>
            <div className={styles.dateRangeSection}>
              <div className={styles.dateRangeLabel}>
                <Content id={ContentId.ExportTimeRange} />
              </div>
              {maxDateStr && (
                <div className={styles.refreshTimeHint}>
                  <Content id={ContentId.DataAvailableThrough} />{' '}
                  {formatDateForDisplay(maxDateStr)}
                </div>
              )}
              <Stack direction="row" gap="m" alignItems="center">
                <DatePickerInput
                  value={createDateFrom}
                  maxDate={maxDateStr ? new Date(maxDateStr) : undefined}
                  onDateChange={(d) =>
                    setCreateDateFrom(d.toISOString().substring(0, 10))
                  }
                  disabled={isExporting}
                  useSiteTimeZone={false}
                />
                <span className={styles.dateArrow}>&rarr;</span>
                <DatePickerInput
                  value={createDateTo}
                  maxDate={maxDateStr ? new Date(maxDateStr) : undefined}
                  onDateChange={(d) =>
                    setCreateDateTo(d.toISOString().substring(0, 10))
                  }
                  disabled={isExporting}
                  useSiteTimeZone={false}
                />
              </Stack>
              {dateRangeError && (
                <div className={styles.validationError}>{dateRangeError}</div>
              )}
            </div>
          </>
        )}

        {/* Event Preview mode — disclaimer + status */}
        {mode === 'preview' && (
          <div className={styles.previewSection}>
            <p className={styles.previewDisclaimer}>
              <Content id={ContentId.AccountingPreviewDisclaimerBody} />
            </p>
            {isPreviewPending && (
              <div className={styles.spinnerContainer}>
                <PosSpinner />
              </div>
            )}
            {isPreviewError && (
              <p className={styles.previewError}>{previewErrorFallback}</p>
            )}
          </div>
        )}
      </div>
    </GenericDialog>
  );
};
